import { Link, useLocation } from "wouter";
import { Mail, Music, Instagram, Youtube, Twitter, DollarSign, ChevronRight, ExternalLink, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Footer() {
  const [currentLocation] = useLocation();

  const handleLinkClick = (href: string) => {
    // If the link is to the current page, scroll to top smoothly
    if (href === currentLocation) {
      const mainElement = document.querySelector('main.overflow-y-auto');
      if (mainElement) {
        mainElement.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
    // For different pages, ScrollRestoration will handle it
  };

  return (
    <footer className="bg-black text-white">
      {/* Wave separator at the top of the footer */}
      <div className="relative h-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/40 to-primary/20"></div>
        <svg
          className="absolute bottom-0 w-full"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,64L48,64C96,64,192,64,288,80C384,96,480,128,576,128C672,128,768,96,864,80C960,64,1056,64,1152,64C1248,64,1344,64,1392,64L1440,64L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
            fill="black"
          />
        </svg>
      </div>

      <div className="container mx-auto px-4 pt-12 pb-8">
        {/* Top section with brand and slogan */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-16">
          <div className="flex items-center mb-6 md:mb-0">
            <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center text-black mr-4 shadow-[0_0_15px_rgba(103,234,82,0.5)]">
              <Music className="h-7 w-7" />
            </div>
            <div>
              <h3 className="text-2xl font-bold">NoLicense Nearby</h3>
              <p className="text-gray-400 text-sm mt-1">Premium music licensing platform</p>
            </div>
          </div>
          
          <div>
            <Link href="/license">
              <Button className="bg-primary hover:bg-primary/90 text-black px-6">
                License Music Now
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Slogan */}
        <div className="text-center mb-8">
          <h2 className="font-bold text-3xl md:text-4xl mb-4">
            <span className="text-primary">500+ Tracks</span> Ready For Your Next Project
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Original music licensing for creators, by a creator. No copyright claims, just creative freedom.
          </p>
        </div>
        
        {/* Main footer content - Links columns with Partner Program centered */}
        <div className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-10 md:gap-x-8">
            {/* Quick Links Column */}
            <div className="flex justify-center">
              <div className="max-w-xs w-full">
                <h4 className="text-xl font-medium mb-6 text-primary inline-flex items-center">
                  Quick Links <ChevronRight className="ml-2 h-4 w-4" />
                </h4>
                <ul className="space-y-3">
                  {[
                    { href: "/catalog", label: "Music Catalog" },
                    { href: "/pricing", label: "Pricing" },
                    { href: "/license", label: "License Music" },
                    { href: "/about", label: "About" }
                  ].map((link, i) => (
                    <li key={i}>
                      <Link href={link.href} onClick={() => handleLinkClick(link.href)}>
                        <span className="text-gray-300 hover:text-primary transition-colors cursor-pointer flex items-center group">
                          <span className="w-2 h-2 rounded-full bg-primary mr-3 group-hover:scale-150 transition-transform duration-200"></span>
                          {link.label}
                          <ChevronRight className="ml-auto opacity-0 group-hover:opacity-100 h-4 w-4 transition-opacity" />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Partner Program - Centered */}
            <div className="flex justify-center">
              <div className="max-w-xs w-full">
                <div className="text-center mb-5">
                  <h4 className="text-xl font-medium text-primary inline-flex items-center">
                    Partner Program <ChevronRight className="ml-2 h-4 w-4" />
                  </h4>
                </div>
                <Link 
                  href="/referral" 
                  className="block" 
                  onClick={() => handleLinkClick("/referral")}
                >
                  <div className="bg-gradient-to-br from-black to-gray-900 p-6 rounded-xl border border-gray-800 shadow-[0_5px_30px_rgba(103,234,82,0.1)] relative overflow-hidden">
                    {/* Corner ribbon */}
                    <div className="absolute top-0 right-0 overflow-hidden w-24 h-24">
                      <div className="absolute transform rotate-45 bg-primary text-black font-bold py-1 right-[-40px] top-[24px] w-[170px] text-center text-sm shadow-md">
                        EARN MONEY
                      </div>
                    </div>
                    
                    <h5 className="font-semibold text-xl mb-3 text-white">Become a Partner</h5>
                    <div className="flex items-start mb-6">
                      <DollarSign className="h-5 w-5 mr-3 text-primary flex-shrink-0 mt-1" />
                      <p className="text-gray-300 text-sm">Earn commission by referring music licensing deals to NoLicense Nearby.</p>
                    </div>
                    <Button className="bg-primary hover:bg-primary/90 text-black w-full font-medium py-5 rounded-lg text-base group overflow-hidden relative">
                      <span className="group-hover:-translate-y-10 transition-transform block duration-200">Apply Now</span>
                      <span className="absolute inset-0 flex items-center justify-center translate-y-10 group-hover:translate-y-0 transition-transform duration-200">Get Started <ExternalLink className="ml-2 h-4 w-4" /></span>
                    </Button>
                  </div>
                </Link>
              </div>
            </div>

            {/* Resources Column */}
            <div className="flex justify-center">
              <div className="max-w-xs w-full">
                <h4 className="text-xl font-medium mb-6 text-primary inline-flex items-center">
                  Resources <ChevronRight className="ml-2 h-4 w-4" />
                </h4>
                <ul className="space-y-3">
                  {[
                    { href: "/licensing-guide", label: "Licensing Guide" },
                    { href: "/terms", label: "Terms of Service" },
                    { href: "/privacy", label: "Privacy Policy" },
                    { href: "/faq", label: "Frequently Asked Questions" }
                  ].map((link, i) => (
                    <li key={i}>
                      <Link href={link.href} onClick={() => handleLinkClick(link.href)}>
                        <span className="text-gray-300 hover:text-primary transition-colors cursor-pointer flex items-center group">
                          <span className="w-2 h-2 rounded-full bg-primary mr-3 group-hover:scale-150 transition-transform duration-200"></span>
                          {link.label}
                          <ChevronRight className="ml-auto opacity-0 group-hover:opacity-100 h-4 w-4 transition-opacity" />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom copyright section with subtle gradient separator */}
        <div className="relative">
          <div className="h-px w-full bg-gradient-to-r from-transparent via-gray-800 to-transparent mb-8"></div>
          
          <div className="text-center">
            <div className="flex flex-col items-center justify-center mb-4">
              <p className="text-gray-400 text-sm flex items-center">
                © {new Date().getFullYear()} NoLicense Nearby. All rights reserved.
                <span className="mx-2 text-gray-600">•</span>
                Made with <Heart className="h-3 w-3 mx-1 text-primary" /> for creators
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
